import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, setText } from "./drawing";

const ROUND_SECONDS = 60;
const MAX_BREACHES = 6;
const SCAN_RADIUS = 150;
const SCAN_COOLDOWN = 0.35;
const FIELD = { left: 60, top: 150, right: 1220, bottom: 660 };

type NodeKind = "shadow" | "sanctioned";

interface AppNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  kind: NodeKind;
  age: number;
  lifetime: number;
}

export class RogueAppCityGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0xc5b0);
  private readonly nodes: AppNode[] = [];
  private playerX = 640;
  private playerY = 400;
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private spawnTimer = 0.5;
  private scanCooldown = 0;
  private scanPulse = 0;
  private breaches = 0;
  private message = "Scan shadow IT before it reaches the data store";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.scanCooldown = Math.max(0, this.scanCooldown - deltaSeconds);
    this.scanPulse = Math.max(0, this.scanPulse - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Scan shadow IT before it reaches the data store";

    if (this.time === 0) {
      this.finish("City secured");
      return;
    }

    this.updatePlayer(deltaSeconds);
    if (this.services.input.consumePressed("primary") && this.scanCooldown === 0) this.scan();
    this.updateNodes(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    this.drawCity(context);
    for (const node of this.nodes) this.drawNode(context, node);
    this.drawScanner(context);
    this.drawHeader(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.02)");
  }

  getHud(): GameHud {
    return { score: this.score, time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private updatePlayer(deltaSeconds: number): void {
    const input = this.services.input;
    const horizontal = Number(input.isDown("right")) - Number(input.isDown("left"));
    const vertical = Number(input.isDown("down")) - Number(input.isDown("up"));
    const pointer = input.pointer;
    if (pointer && pointer.active && horizontal === 0 && vertical === 0) {
      this.playerX = clamp(pointer.x, FIELD.left, FIELD.right);
      this.playerY = clamp(pointer.y, FIELD.top, FIELD.bottom);
      return;
    }
    const speed = 520;
    this.playerX = clamp(this.playerX + horizontal * speed * deltaSeconds, FIELD.left, FIELD.right);
    this.playerY = clamp(this.playerY + vertical * speed * deltaSeconds, FIELD.top, FIELD.bottom);
  }

  private scan(): void {
    this.scanCooldown = SCAN_COOLDOWN;
    this.scanPulse = 0.32;
    let caught = 0;
    let falsePositive = false;
    for (let index = this.nodes.length - 1; index >= 0; index -= 1) {
      const node = this.nodes[index];
      if (!node) continue;
      const distance = Math.hypot(node.x - this.playerX, node.y - this.playerY);
      if (distance > SCAN_RADIUS) continue;
      if (node.kind === "shadow") {
        this.nodes.splice(index, 1);
        this.score += 150;
        caught += 1;
      } else {
        falsePositive = true;
      }
    }

    if (caught > 0) {
      this.services.audio.play("hit");
      this.setMessage(caught > 1 ? `${caught} shadow apps quarantined` : "Shadow app quarantined", 1.3);
    } else if (falsePositive) {
      this.score = Math.max(0, this.score - 30);
      this.services.audio.play("miss");
      this.setMessage("Sanctioned app flagged - avoid false positives", 1.5);
    } else {
      this.services.audio.play("move");
    }
  }

  private updateNodes(deltaSeconds: number): void {
    const cap = 9;
    this.spawnTimer -= deltaSeconds;
    if (this.spawnTimer <= 0 && this.nodes.length < cap) {
      this.spawnNode();
      this.spawnTimer = Math.max(0.42, 0.95 - this.elapsed * 0.006) + this.random.between(0, 0.3);
    }

    for (let index = this.nodes.length - 1; index >= 0; index -= 1) {
      const node = this.nodes[index];
      if (!node) continue;
      node.age += deltaSeconds;
      node.x += node.vx * deltaSeconds;
      node.y += node.vy * deltaSeconds;
      if (node.x < FIELD.left || node.x > FIELD.right) node.vx *= -1;
      if (node.y < FIELD.top || node.y > FIELD.bottom) node.vy *= -1;
      node.x = clamp(node.x, FIELD.left, FIELD.right);
      node.y = clamp(node.y, FIELD.top, FIELD.bottom);

      if (node.age < node.lifetime) continue;
      this.nodes.splice(index, 1);
      if (node.kind === "shadow") {
        this.breaches += 1;
        this.services.audio.play("miss");
        this.setMessage(`Shadow app exfiltrated - breach ${this.breaches}/${MAX_BREACHES}`, 1.8);
        if (this.breaches >= MAX_BREACHES) {
          this.finish("City data compromised");
          return;
        }
      } else {
        this.score += 20;
      }
    }
  }

  private spawnNode(): void {
    const kind: NodeKind = this.random.next() < 0.62 ? "shadow" : "sanctioned";
    const angle = this.random.between(0, Math.PI * 2);
    const speed = this.random.between(60, 130);
    this.nodes.push({
      x: this.random.between(FIELD.left + 40, FIELD.right - 40),
      y: this.random.between(FIELD.top + 40, FIELD.bottom - 40),
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      kind,
      age: 0,
      lifetime: kind === "shadow" ? this.random.between(4.2, 6) : this.random.between(5, 7),
    });
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private drawCity(context: CanvasRenderingContext2D): void {
    const background = context.createLinearGradient(0, 0, 0, 720);
    background.addColorStop(0, "#0a1022");
    background.addColorStop(0.55, "#111a30");
    background.addColorStop(1, "#070b16");
    context.fillStyle = background;
    context.fillRect(0, 0, 1280, 720);

    context.strokeStyle = "rgba(120, 150, 190, 0.12)";
    context.lineWidth = 1.5;
    for (let x = FIELD.left; x <= FIELD.right; x += 80) {
      context.beginPath();
      context.moveTo(x, FIELD.top);
      context.lineTo(x, FIELD.bottom);
      context.stroke();
    }
    for (let y = FIELD.top; y <= FIELD.bottom; y += 80) {
      context.beginPath();
      context.moveTo(FIELD.left, y);
      context.lineTo(FIELD.right, y);
      context.stroke();
    }
    context.strokeStyle = "rgba(66, 226, 195, 0.4)";
    context.lineWidth = 2;
    context.strokeRect(FIELD.left, FIELD.top, FIELD.right - FIELD.left, FIELD.bottom - FIELD.top);
  }

  private drawNode(context: CanvasRenderingContext2D, node: AppNode): void {
    const shadow = node.kind === "shadow";
    const accent = shadow ? "#ff5d5d" : "#42e2c3";
    context.save();
    context.translate(node.x, node.y);
    context.shadowColor = accent;
    context.shadowBlur = 16;
    context.fillStyle = "rgba(7, 13, 22, 0.9)";
    context.strokeStyle = accent;
    context.lineWidth = 3;
    context.beginPath();
    if (shadow) {
      for (let side = 0; side < 6; side += 1) {
        const angle = Math.PI / 6 + side * (Math.PI / 3);
        const px = Math.cos(angle) * 22;
        const py = Math.sin(angle) * 22;
        if (side === 0) context.moveTo(px, py);
        else context.lineTo(px, py);
      }
      context.closePath();
    } else {
      context.arc(0, 0, 20, 0, Math.PI * 2);
    }
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    setText(context, 11, 900, accent, "center");
    context.fillText(shadow ? "SHADOW" : "OK", 0, shadow ? 34 : 32);
    context.restore();
  }

  private drawScanner(context: CanvasRenderingContext2D): void {
    context.save();
    context.translate(this.playerX, this.playerY);
    if (this.scanPulse > 0) {
      const progress = 1 - this.scanPulse / 0.32;
      context.strokeStyle = `rgba(47, 227, 255, ${0.5 * (1 - progress)})`;
      context.lineWidth = 4;
      context.beginPath();
      context.arc(0, 0, SCAN_RADIUS * progress, 0, Math.PI * 2);
      context.stroke();
    }
    context.strokeStyle = "rgba(47, 227, 255, 0.35)";
    context.lineWidth = 1.5;
    context.setLineDash([6, 8]);
    context.beginPath();
    context.arc(0, 0, SCAN_RADIUS, 0, Math.PI * 2);
    context.stroke();
    context.setLineDash([]);

    context.shadowColor = "#2fe3ff";
    context.shadowBlur = 20;
    context.fillStyle = "#0b2238";
    context.strokeStyle = "#2fe3ff";
    context.lineWidth = 4;
    context.beginPath();
    context.arc(0, 0, 20, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.strokeStyle = "#f7fbff";
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(-9, 0);
    context.lineTo(9, 0);
    context.moveTo(0, -9);
    context.lineTo(0, 9);
    context.stroke();
    context.restore();
  }

  private drawHeader(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 330, 84, "#ff7a1a");
    setText(context, 12, 850, "#ffca8a");
    context.fillText("CASB / SHADOW IT", 72, 70);
    setText(context, 20, 900, "#f7fbff");
    context.fillText("APPLICATION CONTROL", 72, 102);

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("DATA STORE INTEGRITY", 1232, 60);
    for (let index = 0; index < MAX_BREACHES; index += 1) {
      const x = 1006 + index * 38;
      context.fillStyle = index < MAX_BREACHES - this.breaches ? "#42e2c3" : "#3c4651";
      context.beginPath();
      context.arc(x, 92, 10, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 620, 1196, 70, "#ff7a1a");
    setText(context, 22, 850, "#f7fbff");
    context.fillText(this.message, 72, 662);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("DRIVE  ARROWS / WASD     SCAN  SPACE", 1204, 662);
  }
}
